import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, apiErr } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspace(data.workspace);
      return data.user;
    } catch {
      setUser(false);
      setWorkspace(null);
      return false;
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!localStorage.getItem("sc_token")) {
        setUser(false);
        setLoading(false);
        return;
      }
      await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe]);

  const finalize = async (data) => {
    setToken(data.access_token);
    await refreshMe();
    return data;
  };

  const login = async (email, password, mfa_code) => {
    const { data } = await api.post("/auth/login", { email, password, mfa_code });
    if (data.mfa_required) return { mfa_required: true };
    return finalize(data);
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return finalize(data);
  };

  const magicVerify = async (token) => {
    const { data } = await api.post("/auth/magic/verify", { token });
    return finalize(data);
  };

  const acceptInvite = async (token, password) => {
    const { data } = await api.post("/auth/accept-invite", { token, password });
    return finalize(data);
  };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password });
    return finalize(data);
  };

  const changeEmail = async (new_email, current_password) => {
    const { data } = await api.post("/auth/change-email", { new_email, current_password });
    return finalize(data);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
    setWorkspace(null);
  };

  return (
    <AuthContext.Provider value={{ user, workspace, loading, login, register, magicVerify, acceptInvite, logout, refreshMe, changePassword, changeEmail, apiErr }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
